import React from 'react';
import { Button, Checkbox, Col, Divider, Form, Input, InputNumber, Row, Select, Space, Spin, Tag, Tooltip, TreeSelect, Typography } from 'antd';
import { openNotification } from '../../../utils';
import { listReportNodesAll } from '../../report-nodes/services/report-nodeService';
import { createQuestion, updateQuestion, getQuestion } from '../services/questionService';
import AnswerList from '../../answers/views/List';
import { CreateQuestionDto } from '../types/Question';
import useFunctionTagSuggestions, { NamedFunctionTag } from '../../function-tags/hooks/useFunctionTagSuggestions';
import { apiFetch } from 'services/apiClient';

const TableQuestionList = React.lazy(() => import('../../table-questions/views/List'));

const HIDDEN_FIELDS_QUESTION_TYPES = new Set<string>([
  'TITLE',
  'ADDRESS',
  'SGK_REGISTRATION_NUMBER',
  'PHONE',
  'CONTRACT_ID',
  'EMAIL',
  'FIRST_NAME',
  'LAST_NAME',
  'PROFESSION',
  'DIPLOMA_DATE',
  'DIPLOMA_NUMBER',
  'REGISTRATION_NUMBER',
  'IDENTITY_NUMBER',
  'REGISTRATION_DATE',
  'REPORT_NUMBER',
  'REPORT_DATE',
  'CONTROL_START_DATE',
  'CONTROL_END_DATE',
  'NEXT_CONTROL_DATE'
]);

const NORMAL_MIN_COLUMN = 1;
const TABLE_COLUMN_MIN_COLUMN = 2;
const MAX_COLUMN = 24;
const DEFAULT_BULK_INPUT_MODE = 'PER_REPORT';
const BULK_INPUT_MODE_OPTIONS = new Set(['SHARED', 'PER_REPORT']);

export type QuestionFormSuccessPayload = {
  id?: number;
  questionType?: string;
  mode: 'create' | 'update';
};

interface QuestionFormProps {
  mode?: 'question' | 'table-column';
  id?: number;
  tableQuestionId?: number;
  tableColumnId?: number;
  onSuccess?: (payload: QuestionFormSuccessPayload) => void;
  referenceId?: number;
  reportFormatId?: number;
  availableFunctionTags?: string[];
  attributeFunctionTags?: NamedFunctionTag[];
  toolFunctionTags?: NamedFunctionTag[];
  toolId?: number;
  attributeId?: number;
  reportAttributeId?: number;
  hideAnswers?: boolean;
}

type StaticValueLike = { name?: string | null; functionTag?: string | null; isActive?: boolean | null; active?: boolean | null };

const mapStaticValuesToTags = (items: StaticValueLike[] | undefined): NamedFunctionTag[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  const map = new Map<string, NamedFunctionTag>();
  items.forEach((item) => {
    const active = item?.isActive ?? item?.active;
    if (active === false) {
      return;
    }
    const rawTag = String(item?.functionTag ?? '').trim();
    const fallbackName = String(item?.name ?? '').trim();
    const resolvedValue = rawTag || fallbackName.replace(/\s+/g, '').toUpperCase();
    const normalizedValue = resolvedValue.trim();
    if (!normalizedValue) {
      return;
    }
    const key = normalizedValue.toUpperCase();
    if (!map.has(key)) {
      map.set(key, { name: fallbackName || normalizedValue, value: normalizedValue });
    }
  });
  return Array.from(map.values());
};

const mergeFunctionTags = (...groups: Array<NamedFunctionTag[] | undefined>): NamedFunctionTag[] => {
  const map = new Map<string, NamedFunctionTag>();
  groups
    .filter(Boolean)
    .forEach((group) => {
      (group as NamedFunctionTag[]).forEach((item) => {
        const value = String(item?.value ?? '').trim();
        if (!value) {
          return;
        }
        const key = value.toUpperCase();
        if (!map.has(key)) {
          map.set(key, { name: item?.name ?? value, value });
        }
      });
    });
  return Array.from(map.values());
};

export default function QuestionForm({
  mode = 'question',
  id,
  tableQuestionId,
  tableColumnId,
  onSuccess,
  referenceId,
  reportFormatId,
  availableFunctionTags,
  attributeFunctionTags,
  toolFunctionTags,
  toolId,
  attributeId,
  reportAttributeId,
  hideAnswers = false,
}: QuestionFormProps) {
  const [form] = Form.useForm<CreateQuestionDto>();
  const isTableColumnMode = mode === 'table-column';
  const effectiveId = isTableColumnMode ? tableColumnId : id;
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [reportNodeOptions, setReportNodeOptions] = React.useState<any[]>([]);
  const [currentSortOrder, setCurrentSortOrder] = React.useState<number | undefined>(undefined);
  const [resolvedAttributeFunctionTags, setResolvedAttributeFunctionTags] = React.useState<NamedFunctionTag[]>(
    attributeFunctionTags ?? [],
  );
  const [resolvedToolFunctionTags, setResolvedToolFunctionTags] = React.useState<NamedFunctionTag[]>(
    toolFunctionTags ?? [],
  );
  const [controlAreaOptions, setControlAreaOptions] = React.useState<any[]>([]);
  const { functionTagGroups, appendFunctionTag } = useFunctionTagSuggestions({
    availableFunctionTags,
    attributeFunctionTags: resolvedAttributeFunctionTags,
    toolFunctionTags: resolvedToolFunctionTags,
    getFunctionText: () => form.getFieldValue('functionText'),
    setFunctionText: (value: string) => form.setFieldsValue({ functionText: value }),
  });
  const treeData = React.useMemo(() => {
    const getParentId = (node: any) => {
      const parent = node?.parent;
      return typeof parent === 'object' ? parent?.id : parent;
    };

    const normalizeSortOrder = (node: any) =>
      Number.isFinite(Number(node?.sortOrder)) ? Number(node.sortOrder) : 0;

    const compareNodes = (a: any, b: any) => {
      const orderDiff = normalizeSortOrder(a) - normalizeSortOrder(b);
      if (orderDiff !== 0) return orderDiff;
      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    };

    const nodes = (reportNodeOptions || []).filter((n: any) => typeof n?.id === 'number');
    if (nodes.length === 0) return [];

    const byId = new Map<number, any>();
    nodes.forEach(n => {
      byId.set(n.id, n);
    });

    const childrenByParent = new Map<number, any[]>();
    const roots: any[] = [];

    nodes.forEach(n => {
      const parentId = getParentId(n);
      if (parentId != null && byId.has(parentId)) {
        const siblings = childrenByParent.get(parentId) ?? [];
        siblings.push(n);
        childrenByParent.set(parentId, siblings);
        return;
      }
      roots.push(n);
    });

    const result: any[] = [];

    const visit = (node: any) => {
      result.push({
        id: node.id,
        pId: getParentId(node),
        value: node.id,
        title: node.title ?? String(node.id),
      });
      const children = childrenByParent.get(node.id);
      if (!children || children.length === 0) return;
      children.sort(compareNodes).forEach(visit);
    };

    roots.sort(compareNodes).forEach(visit);
    return result;
  }, [reportNodeOptions]);
  const questionType = Form.useWatch('questionType', form);
  const isCurrentYear = Form.useWatch('isCurrentYear', form);
  const isChecked = Form.useWatch('isChecked', form);
  const watchedCol = Form.useWatch('col', form);
  const watchedTitleCol = Form.useWatch('titleCol', form);
  const watchedToolId = Form.useWatch('toolId', form);
  const watchedAttributeId = Form.useWatch('attributeId', form);
  const minColumn = isTableColumnMode ? TABLE_COLUMN_MIN_COLUMN : NORMAL_MIN_COLUMN;
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);
  const allowNodeSelection = !isTableColumnMode && toolId === undefined && attributeId === undefined;
  const nodeFieldRules = React.useMemo(
    () => (allowNodeSelection || referenceId !== undefined ? [{ required: true }] : undefined),
    [allowNodeSelection, referenceId],
  );
  const normalizedQuestionType = React.useMemo(() => {
    if (questionType === undefined || questionType === null) {
      return undefined;
    }
    return String(questionType).toUpperCase();
  }, [questionType]);
  const hideAdditionalFields = normalizedQuestionType
    ? HIDDEN_FIELDS_QUESTION_TYPES.has(normalizedQuestionType)
    : false;
  const showFixedRowCount = !isTableColumnMode && normalizedQuestionType === 'TABLE';
  const normalizedCol = React.useMemo(() => {
    if (watchedCol === undefined || watchedCol === null) {
      return undefined;
    }
    const numeric = Number(watchedCol);
    return Number.isFinite(numeric) ? numeric : undefined;
  }, [watchedCol]);
  const normalizedTitleCol = React.useMemo(() => {
    if (watchedTitleCol === undefined || watchedTitleCol === null) {
      return undefined;
    }
    const numeric = Number(watchedTitleCol);
    return Number.isFinite(numeric) ? numeric : undefined;
  }, [watchedTitleCol]);
  const colOptions = React.useMemo(() => {
    const maxSelectableColumn = !isTableColumnMode && normalizedTitleCol !== undefined
      ? Math.max(minColumn, MAX_COLUMN - normalizedTitleCol)
      : MAX_COLUMN;
    return Array.from({ length: Math.max(0, maxSelectableColumn - minColumn + 1) }, (_, i) => minColumn + i);
  }, [isTableColumnMode, minColumn, normalizedTitleCol]);
  const titleColOptions = React.useMemo(() => {
    if (normalizedCol === undefined) {
      return Array.from({ length: MAX_COLUMN - minColumn }, (_, index) => index + 1);
    }
    const limit = Math.max(0, MAX_COLUMN - normalizedCol);
    return Array.from({ length: limit }, (_, index) => index + 1);
  }, [minColumn, normalizedCol]);
  const isCurrentDay = Form.useWatch('isCurrentDay', form);
  const isCurrentYearChecked = normalizedQuestionType === 'YEAR' && isCurrentYear === true;
  const isCurrentDayChecked = normalizedQuestionType === 'DATE' && isCurrentDay === true;
  const handleUseCurrentYearMaxChange = React.useCallback(
    (event: { target?: { checked?: boolean } }) => {
      const checked = event?.target?.checked === true;
      const updates: Partial<CreateQuestionDto> = { isCurrentYear: checked };
      if (checked) {
        updates.maxValue = undefined;
      }
      form.setFieldsValue(updates);
    },
    [form],
  );
  const handleUseCurrentDayChange = React.useCallback(
    (event: { target?: { checked?: boolean } }) => {
      form.setFieldsValue({ isCurrentDay: event?.target?.checked === true });
    },
    [form],
  );
  React.useEffect(() => {
    let cancelled = false;
    const initializeForm = async () => {
      setInitialLoading(true);
      try {
        if (effectiveId) {
          const d = await getQuestion(effectiveId);
          if (cancelled) {
            return;
          }
          const data: any = { ...d };
          if (data.node) data.node = typeof data.node === 'object' ? data.node.id : data.node;
          if (data.col !== undefined && data.col !== null) data.col = Number(data.col);
          if (data.titleCol !== undefined && data.titleCol !== null) {
            data.titleCol = Number(data.titleCol);
          }
          if (data.tool) data.toolId = typeof data.tool === 'object' ? data.tool?.id : data.tool;
          if (data.attribute) data.attributeId = typeof data.attribute === 'object' ? data.attribute?.id : data.attribute;
          if (data.controlAreas) data.controlAreas = data.controlAreas.map((o: any) => o.id ?? o);
          if (data.sortOrder !== undefined && data.sortOrder !== null) setCurrentSortOrder(Number(data.sortOrder));
          data.isVisible = data.isVisible ?? true;
          data.excludeFromInspectorForm = data.excludeFromInspectorForm ?? false;
          data.allowMultipleAnswers = data.allowMultipleAnswers ?? false;
          data.showAllAnswersInReport = data.showAllAnswersInReport ?? false;
          data.countable = data.countable ?? false;
          data.bulkInputMode = BULK_INPUT_MODE_OPTIONS.has(String(data.bulkInputMode ?? '').toUpperCase())
            ? String(data.bulkInputMode).toUpperCase()
            : DEFAULT_BULK_INPUT_MODE;
          form.setFieldsValue(data);
        } else {
          form.resetFields();
          const defaultValues: Partial<CreateQuestionDto> = {
            questionType: 'FREETEXT',
            col: minColumn,
            isVisible: true,
            excludeFromInspectorForm: false,
            allowMultipleAnswers: false,
            showAllAnswersInReport: false,
            countable: false,
            bulkInputMode: DEFAULT_BULK_INPUT_MODE,
            isCurrentYear: false,
            isCurrentDay: false,
          };
          if (!isTableColumnMode && referenceId !== undefined) {
            defaultValues.node = referenceId;
          }
          if (!isTableColumnMode && toolId !== undefined) {
            defaultValues.toolId = toolId;
          }
          if (!isTableColumnMode && attributeId !== undefined) {
            defaultValues.attributeId = attributeId;
          }
          if (isTableColumnMode && tableQuestionId !== undefined) {
            (defaultValues as any).parentQuestionId = tableQuestionId;
          }
          form.setFieldsValue(defaultValues);
          setCurrentSortOrder(undefined);
        }
        if (allowNodeSelection) {
          const query = reportFormatId !== undefined ? 'reportFormatId.equals=' + reportFormatId : undefined;
          const reportNodes = await listReportNodesAll(query);
          if (!cancelled) {
            setReportNodeOptions(reportNodes);
          }
        } else {
          setReportNodeOptions([]);
        }
      } catch (error) {
        console.error('Soru formu yüklenemedi:', error);
        if (!cancelled) {
          openNotification('error', 'Hata', 'Soru formu yüklenemedi');
        }
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };
    initializeForm();
    return () => {
      cancelled = true;
    };
  }, [effectiveId, form, reportFormatId, referenceId, toolId, attributeId, tableQuestionId, allowNodeSelection, isTableColumnMode, minColumn]);
  React.useEffect(() => {
    const currentTitleCol = form.getFieldValue('titleCol');
    const currentCol = form.getFieldValue('col');
    if (
      currentTitleCol !== undefined
      && currentTitleCol !== null
      && currentTitleCol !== ''
      && currentCol !== undefined
      && currentCol !== null
      && currentCol !== ''
    ) {
      const numericTitleCol = Number(currentTitleCol);
      const numericCol = Number(currentCol);
      if (Number.isFinite(numericTitleCol) && Number.isFinite(numericCol) && numericTitleCol + numericCol > MAX_COLUMN) {
        form.setFieldsValue({ col: undefined });
      }
    }
  }, [normalizedCol, normalizedTitleCol, isTableColumnMode, form]);
  React.useEffect(() => {
    if (!isChecked || hideAdditionalFields) {
      form.setFieldsValue({ checkReminderText: undefined });
    }
  }, [isChecked, hideAdditionalFields, form]);
  React.useEffect(() => {
    if (normalizedQuestionType !== 'FUNCTION') {
      form.setFieldsValue({ functionText: undefined });
    }
  }, [normalizedQuestionType, form]);
  React.useEffect(() => {
    if (normalizedQuestionType !== 'MULTIPLE') {
      form.setFieldsValue({ allowMultipleAnswers: false, showAllAnswersInReport: false });
    }
  }, [normalizedQuestionType, form]);
  React.useEffect(() => {
    if (normalizedQuestionType !== 'YEAR') {
      form.setFieldsValue({ isCurrentYear: false });
    }
  }, [normalizedQuestionType, form]);
  React.useEffect(() => {
    if (normalizedQuestionType !== 'DATE') {
      form.setFieldsValue({ isCurrentDay: false });
    }
  }, [normalizedQuestionType, form]);
  React.useEffect(() => {
    if (!showFixedRowCount) {
      form.setFieldsValue({ fixedRowCount: undefined });
    }
  }, [showFixedRowCount, form]);
  React.useEffect(() => {
    if (isTableColumnMode || !hideAdditionalFields) {
      return;
    }
    const currentValues = form.getFieldsValue(['col', 'isChecked', 'checkReminderText', 'node', 'isCurrentYear', 'isCurrentDay']);
    const updates: Partial<CreateQuestionDto> = {};
    const normalizedCurrentCol =
      currentValues?.col !== undefined && currentValues?.col !== null && currentValues?.col !== ''
        ? Number(currentValues.col)
        : undefined;
    if (normalizedCurrentCol === undefined || !Number.isFinite(normalizedCurrentCol) || normalizedCurrentCol < minColumn) {
      updates.col = minColumn;
    }
    if (
      (currentValues?.node === undefined || currentValues?.node === null || currentValues?.node === '') &&
      referenceId !== undefined
    ) {
      updates.node = referenceId;
    }
    if (currentValues?.isChecked) {
      updates.isChecked = false;
    }
    if (currentValues?.checkReminderText) {
      updates.checkReminderText = undefined;
    }
    if (currentValues?.isCurrentYear) {
      updates.isCurrentYear = false;
    }
    if (currentValues?.isCurrentDay) {
      updates.isCurrentDay = false;
    }
    if (Object.keys(updates).length) {
      form.setFieldsValue(updates);
    }
  }, [hideAdditionalFields, form, referenceId, isTableColumnMode, minColumn]);
  React.useEffect(() => {
    setResolvedAttributeFunctionTags(attributeFunctionTags ?? []);
  }, [attributeFunctionTags]);
  React.useEffect(() => {
    setResolvedToolFunctionTags(toolFunctionTags ?? []);
  }, [toolFunctionTags]);

  React.useEffect(() => {
    const fetchToolTags = async (targetId?: number) => {
      if (!targetId) {
        setResolvedToolFunctionTags(mergeFunctionTags(toolFunctionTags));
        return;
      }
      try {
        const response = await apiFetch(`/tools/${targetId}/static-values`);
        const normalized = Array.isArray(response) ? response : response?.data;
        const mapped = mapStaticValuesToTags(normalized);
        setResolvedToolFunctionTags(mergeFunctionTags(toolFunctionTags, mapped));
      } catch (error) {
        console.error('Bileşen sabit değerleri alınamadı:', error);
        setResolvedToolFunctionTags(mergeFunctionTags(toolFunctionTags));
      }
    };
    const nextToolId = watchedToolId ?? toolId;
    fetchToolTags(nextToolId ? Number(nextToolId) : undefined);
  }, [watchedToolId, toolId, toolFunctionTags]);

  React.useEffect(() => {
    const fetchAttributeTags = async (targetId?: number) => {
      if (!targetId) {
        setResolvedAttributeFunctionTags(mergeFunctionTags(attributeFunctionTags));
        return;
      }
      try {
        const response = await apiFetch(`/attributes/${targetId}/static-values`);
        const normalized = Array.isArray(response) ? response : response?.data;
        const mapped = mapStaticValuesToTags(normalized);
        setResolvedAttributeFunctionTags(mergeFunctionTags(attributeFunctionTags, mapped));
      } catch (error) {
        console.error('Parametre sabit değerleri alınamadı:', error);
        setResolvedAttributeFunctionTags(mergeFunctionTags(attributeFunctionTags));
      }
    };
    const nextAttributeId = watchedAttributeId ?? attributeId;
    fetchAttributeTags(nextAttributeId ? Number(nextAttributeId) : undefined);
  }, [watchedAttributeId, attributeId, attributeFunctionTags]);
  const controlAreaAttributeId = React.useMemo(() => {
    if (reportAttributeId !== undefined && reportAttributeId !== null) {
      return Number(reportAttributeId);
    }
    return undefined;
  }, [reportAttributeId]);

  React.useEffect(() => {
    let cancelled = false;
    const query = controlAreaAttributeId ? `?attributeId=${controlAreaAttributeId}` : '';
    apiFetch(`/control-areas${query}`)
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        const normalized = Array.isArray(response) ? response : response?.data;
        setControlAreaOptions(normalized || []);
      })
      .catch((error) => {
        console.error('Kontrol alanları alınamadı:', error);
        if (!cancelled) {
          setControlAreaOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [controlAreaAttributeId]);

  const onFinish = async (values: CreateQuestionDto) => {
    const submittedQuestionType = String(values.questionType ?? '').toUpperCase();
    const submittedHideAdditionalFields = HIDDEN_FIELDS_QUESTION_TYPES.has(submittedQuestionType);
    const submittedShowFixedRowCount = !isTableColumnMode && submittedQuestionType === 'TABLE';
    const sanitizedFunctionTag =
      values.functionTag === undefined || values.functionTag === null || values.functionTag === ''
        ? undefined
        : String(values.functionTag).trim();
    const sanitizedNode =
      values.node !== undefined && values.node !== null && values.node !== ''
        ? Number(values.node)
        : submittedHideAdditionalFields && referenceId !== undefined
        ? Number(referenceId)
        : undefined;
    const sanitizedCol =
      values.col !== undefined && values.col !== null
        ? Number(values.col)
        : submittedHideAdditionalFields || isTableColumnMode
        ? minColumn
        : undefined;
    const sanitizedTitleCol =
      values.titleCol !== undefined && values.titleCol !== null
        ? Number(values.titleCol)
        : undefined;
    const sanitizedToolId = (() => {
      const raw = (values.toolId ?? toolId) as string | number | null | undefined;
      if (raw === undefined || raw === null) {
        return undefined;
      }
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) {
          return undefined;
        }
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? numeric : undefined;
      }
      const numeric = Number(raw);
      return Number.isFinite(numeric) ? numeric : undefined;
    })();
    const sanitizedAttributeId = (() => {
      const raw = (values.attributeId ?? attributeId) as string | number | null | undefined;
      if (raw === undefined || raw === null) {
        return undefined;
      }
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) {
          return undefined;
        }
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? numeric : undefined;
      }
      const numeric = Number(raw);
      return Number.isFinite(numeric) ? numeric : undefined;
    })();
    const data: any = {
      ...values,
      functionTag: sanitizedFunctionTag,
      bulkInputMode: BULK_INPUT_MODE_OPTIONS.has(String(values.bulkInputMode ?? '').toUpperCase())
        ? String(values.bulkInputMode).toUpperCase()
        : DEFAULT_BULK_INPUT_MODE,
      col: sanitizedCol,
      titleCol: sanitizedTitleCol,
      node: sanitizedNode,
      toolId: sanitizedToolId,
      attributeId: sanitizedAttributeId,
      allowMultipleAnswers: submittedQuestionType === 'MULTIPLE' ? values.allowMultipleAnswers === true : false,
      isCurrentYear: submittedQuestionType === 'YEAR' ? values.isCurrentYear === true : false,
      isCurrentDay: submittedQuestionType === 'DATE' ? values.isCurrentDay === true : false,
      fixedRowCount:
        submittedShowFixedRowCount && Number.isFinite(Number(values.fixedRowCount)) && Number(values.fixedRowCount) > 0
          ? Math.floor(Number(values.fixedRowCount))
          : undefined,
      showAllAnswersInReport: submittedQuestionType === 'MULTIPLE' ? values.showAllAnswersInReport === true : false,
      maxValue:
        submittedQuestionType === 'YEAR' && values.isCurrentYear === true
          ? undefined
          : values.maxValue,
      ...(effectiveId ? { sortOrder: currentSortOrder } : {}),
    };
    if (isTableColumnMode) {
      data.parentQuestionId = tableQuestionId;
      delete data.node;
      delete data.toolId;
      delete data.attributeId;
      delete data.controlAreas;
    }
    if (!isTableColumnMode && values.controlAreas !== undefined) {
      data.controlAreas = Array.isArray(values.controlAreas)
        ? values.controlAreas
            .filter((id) => id !== undefined && id !== null && id !== '')
            .map((id) => Number(id))
        : [];
    }
    try {
      let saved: any = null;
      if (effectiveId) {
        saved = await updateQuestion(effectiveId, data);
        openNotification('success', 'Başarılı', 'Soru Güncellendi');
        onSuccess?.({
          id: saved?.id ?? effectiveId,
          questionType: saved?.questionType ?? values.questionType,
          mode: 'update',
        });
      } else {
        saved = await createQuestion(data);
        openNotification('success', 'Başarılı', 'Soru Eklendi');
        onSuccess?.({
          id: saved?.id,
          questionType: saved?.questionType ?? values.questionType,
          mode: 'create',
        });
      }
    } catch (e) {
      openNotification('error', 'Hata', 'Soru işlemi başarısız');
    }
  };
  return (
    <Spin spinning={initialLoading}>

      <Form form={form} onFinish={onFinish} layout='vertical'>
        {!isTableColumnMode ? (
          <>
            <Form.Item name='toolId' hidden>
              <Input type='hidden' />
            </Form.Item>
            <Form.Item name='attributeId' hidden>
              <Input type='hidden' />
            </Form.Item>
          </>
        ) : null}
        <Form.Item name='isCurrentYear' valuePropName='checked' hidden>
          <Checkbox />
        </Form.Item>
        <Form.Item name='isCurrentDay' valuePropName='checked' hidden>
          <Checkbox />
        </Form.Item>
        {!isTableColumnMode && !allowNodeSelection ? (
          <Form.Item name='node' hidden rules={nodeFieldRules}>
            <Input type='hidden' />
          </Form.Item>
        ) : null}
        <Form.Item name='questionType' label='Soru Tipi' rules={[{ required: true }]}>
          <Select
            showSearch
            filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
            placeholder='Seçiniz'
          >
            <Select.Option value='MULTIPLE'>Çoktan Seçmeli</Select.Option>
            <Select.Option value='FREETEXT'>Serbest Metin</Select.Option>
            <Select.Option value='NETWORK_TYPE'>Şebeke Tipi</Select.Option>
            <Select.Option value='NUMBER'>Sayı</Select.Option>
            <Select.Option value='DATE'>Tarih</Select.Option>
            <Select.Option value='TIME'>Saat</Select.Option>
            <Select.Option value='YEAR'>Yıl</Select.Option>
            <Select.Option value='IMAGE'>Görsel</Select.Option>
            <Select.Option value='FUNCTION'>Fonksiyon</Select.Option>
            <Select.Option value='TITLE'>Ünvan</Select.Option>
            <Select.Option value='PHONE'>Telefon</Select.Option>
            <Select.Option value='ADDRESS'>Adres</Select.Option>
            <Select.Option value='EMAIL'>E-posta</Select.Option>
            <Select.Option value='SGK_REGISTRATION_NUMBER'>SGK Sicil Numarası</Select.Option>
            <Select.Option value='CONTRACT_ID'>İSG Katip Sözleşme ID</Select.Option>
            <Select.Option value='FIRST_NAME'>Ad</Select.Option>
            <Select.Option value='LAST_NAME'>Soyad</Select.Option>
            <Select.Option value='PROFESSION'>Meslek</Select.Option>
            <Select.Option value='DIPLOMA_DATE'>Diploma Tarihi</Select.Option>
            <Select.Option value='DIPLOMA_NUMBER'>Diploma Numarası</Select.Option>
            <Select.Option value='REGISTRATION_NUMBER'>Kalıcı Kayıt Numarası</Select.Option>
            <Select.Option value='IDENTITY_NUMBER'>TC Kimlik No</Select.Option>
            <Select.Option value='REGISTRATION_DATE'>Kalıcı Kayıt Tarihi</Select.Option>
            <Select.Option value='WORK_EQUIPMENT'>İş Ekipmanı</Select.Option>
            <Select.Option value='REPORT_NUMBER'>Rapor Numarası</Select.Option>
            <Select.Option value='REPORT_DATE'>Rapor Tarihi</Select.Option>
            <Select.Option value='CONTROL_START_DATE'>Kontrol Başlangıç Tarihi</Select.Option>
            <Select.Option value='CONTROL_END_DATE'>Kontrol Bitiş Tarihi</Select.Option>
            <Select.Option value='NEXT_CONTROL_DATE'>Sonraki Kontrol Tarihi</Select.Option>
            <Select.Option value='STARTED_AT'>Başlangıç Saati</Select.Option>
            <Select.Option value='FINISHED_AT'>Bitiş Saati</Select.Option>
            {!isTableColumnMode ? <Select.Option value='TABLE'>Tablo Sorusu</Select.Option> : null}
          </Select>
        </Form.Item>
        <Form.Item name='title' label='Başlık' rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name='description' label='Açıklama'>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name='bulkInputMode' label='Veri Giriş Kapsamı' rules={[{ required: true }]}>
          <Select placeholder='Seçiniz'>
            <Select.Option value='PER_REPORT'>Kontrol Noktası Bazlı</Select.Option>
            <Select.Option value='SHARED'>Ortak Bilgi</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name='col'
          label='Sütun'
          rules={[
            { required: true, message: 'Lütfen sütun seçiniz.' },
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === '') {
                  return Promise.resolve();
                }
                const numeric = Number(value);
                if (!Number.isFinite(numeric) || numeric < minColumn || numeric > MAX_COLUMN) {
                  return Promise.reject(new Error(`Sütun ${minColumn} ile ${MAX_COLUMN} arasında olmalıdır.`));
                }
                if (!isTableColumnMode && normalizedTitleCol !== undefined && normalizedTitleCol + numeric > MAX_COLUMN) {
                  return Promise.reject(new Error(`Başlık sütunu ile sütun toplamı ${MAX_COLUMN}'ü geçemez.`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select placeholder='Seçiniz'>
            {colOptions.map(n => (
              <Select.Option key={n} value={n}>{n}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name='titleCol'
          label='Başlık Sütunu'
          rules={[
            { required: true, message: 'Lütfen başlık sütununu seçiniz.' },
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === '') {
                  return Promise.resolve();
                }
                const numeric = Number(value);
                if (!Number.isFinite(numeric) || numeric < 1 || numeric > MAX_COLUMN - minColumn) {
                  return Promise.reject(new Error(`Başlık sütunu 1 ile ${MAX_COLUMN - minColumn} arasında olmalıdır.`));
                }
                if (normalizedCol !== undefined && numeric + normalizedCol > MAX_COLUMN) {
                  return Promise.reject(new Error(`Başlık sütunu ile sütun toplamı ${MAX_COLUMN}'ü geçemez.`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select placeholder='Seçiniz'>
            {titleColOptions.map((n) => (
              <Select.Option key={n} value={n}>{n}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name='isVisible' valuePropName='checked'>
              <Checkbox>Raporda Gözükmeli</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name='excludeFromInspectorForm' valuePropName='checked'>
              <Checkbox>PKM Veri Formunda Gizle</Checkbox>
            </Form.Item>
          </Col>
        </Row>
        {!hideAdditionalFields ? (
          <>
          
            <Form.Item
              name='tag'
              label='Etiket'
            >
              <Input />
            </Form.Item>

            <Form.Item
              name='functionTag'
              label='Fonksiyon Etiketi'
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const rawText = String(value);
                    const trimmed = rawText.trim();
                    if (trimmed.length === 0) {
                      return Promise.reject(new Error('Fonksiyon Etiketi boşluk bırakılamaz.'));
                    }
                    if (rawText !== trimmed) {
                      return Promise.reject(new Error('Fonksiyon Etiketi başında veya sonunda boşluk olamaz.'));
                    }
                    if (/\s/.test(trimmed)) {
                      return Promise.reject(new Error('Fonksiyon Etiketi boşluk içeremez.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>
            {!isTableColumnMode ? (
              <Form.Item name='controlAreas' label='Parametre Tipleri - Özellikleri'>
                <Select
                  mode='multiple'
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
                  }
                  placeholder='Seçiniz'
                  allowClear
                >
                  {controlAreaOptions.map((o: any) => (
                    <Select.Option key={o.id} value={o.id}>
                      {o.name ?? o.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name='isDistinctive' valuePropName='checked'>
                  <Checkbox>Ayırt Edici Bilgi</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name='isRequired' valuePropName='checked'>
                  <Checkbox>Zorunlu mu?</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name='countable' valuePropName='checked'>
                  <Checkbox>Sayılabilir</Checkbox>
                </Form.Item>
              </Col>
              {normalizedQuestionType === 'MULTIPLE' ? (
                <Col xs={24} sm={8}>
                  <Form.Item name='allowMultipleAnswers' valuePropName='checked'>
                    <Checkbox>Birden fazla cevap seçilebilir</Checkbox>
                  </Form.Item>
                </Col>
              ) : null}
              {normalizedQuestionType === 'MULTIPLE' ? (
                <Col xs={24} sm={8}>
                  <Form.Item name='showAllAnswersInReport' valuePropName='checked'>
                    <Checkbox>Raporda tüm cevaplar listelenmeli</Checkbox>
                  </Form.Item>
                </Col>
              ) : null}
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name='isCritical' valuePropName='checked'>
                  <Checkbox>Kritik mi? (Ağır Kusur)</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name='isChecked' valuePropName='checked'>
                  <Checkbox>Kontrol Et</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {isChecked ? (
              <Form.Item
                name='checkReminderText'
                label='Kontrol Hatırlatma Metni'
                rules={[{ required: true, message: 'Kontrol Et seçiliyse bu alan zorunludur.' }]}
              >
                <Input />
              </Form.Item>
            ) : null}
            {questionType === 'FUNCTION' ? (
              <>
                <Form.Item name='functionText' label='Fonksiyon Metni'>
                  <Input />
                </Form.Item>
                {functionTagGroups.length ? (
                  <div style={{ marginTop: -8, marginBottom: 16 }}>
                    {functionTagGroups.map((group) => (
                      <div key={group.key} style={{ marginTop: 12 }}>
                        <Typography.Text strong type='secondary' style={{ marginRight: 8 }}>
                          {group.title}
                        </Typography.Text>
                        <Space wrap size={8} style={{ marginTop: 8 }}>
                          {group.tags.map((item) => (
                            <Tooltip key={item.key} title={item.insertToken}>
                              <Tag
                                key={item.key}
                                color={group.color === 'default' ? undefined : group.color}
                                onClick={() => appendFunctionTag(item.insertToken)}
                                style={{ cursor: 'pointer' }}
                              >
                                <span>{item.label}</span>
                                {/* <span style={{ marginLeft: 6, fontSize: 12, opacity: 0.75 }}>{item.insertToken}</span> */}
                              </Tag>
                            </Tooltip>
                          ))}
                        </Space>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
            {showFixedRowCount ? (
              <Form.Item name='fixedRowCount' label='Sabit Satır Sayısı'>
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            ) : null}
            {(() => {
              const showMinMax = normalizedQuestionType === 'NUMBER' || normalizedQuestionType === 'YEAR';
              const showDigits = normalizedQuestionType === 'NUMBER';
              const showCurrentYearCheckbox = normalizedQuestionType === 'YEAR';
              const showTodayCheckbox = normalizedQuestionType === 'DATE' && !isTableColumnMode;
              if (!showMinMax && !showDigits && !showTodayCheckbox) return null;
              return (
                <Row gutter={16}>
                  {showTodayCheckbox ? (
                    <Col xs={24} sm={12}>
                      <Form.Item label='Varsayılan Tarih'>
                        <Checkbox checked={isCurrentDayChecked} onChange={handleUseCurrentDayChange}>
                          Bugün
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  ) : null}
                  {showMinMax && (
                    <Col xs={24} sm={12}>
                      <Form.Item name='minValue' label='Minimum'>
                        <InputNumber style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  )}
                  {showMinMax && (
                    <Col xs={24} sm={12}>
                      <Form.Item label='Maksimum'>
                        <Space.Compact style={{ width: '100%' }}>
                          {showCurrentYearCheckbox && isCurrentYearChecked ? (
                            <InputNumber style={{ width: '100%' }} value={currentYear} disabled />
                          ) : (
                            <Form.Item name='maxValue' noStyle>
                              <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                          )}
                          {showCurrentYearCheckbox ? (
                            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 8, whiteSpace: 'nowrap' }}>
                              <Checkbox checked={isCurrentYearChecked} onChange={handleUseCurrentYearMaxChange}>
                                Bu yıl
                              </Checkbox>
                            </div>
                          ) : null}
                        </Space.Compact>
                      </Form.Item>
                    </Col>
                  )}
                  {showDigits && (
                    <Col xs={24} sm={8}>
                      <Form.Item name='digits' label='Ondalık Basamak'>
                        <InputNumber min={0} precision={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              );
            })()}
            {allowNodeSelection ? (
              <Form.Item name='node' label='Rapor Yapısı' rules={nodeFieldRules}>
                <TreeSelect
                  treeDataSimpleMode
                  treeData={treeData}
                  showSearch
                  placeholder='Seçiniz'
                  allowClear
                  filterTreeNode={(input, node) =>
                    (node?.title as string)?.toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
                  }
                />
              </Form.Item>
            ) : null}
          </>
        ) : null}
        <Button htmlType='submit' type='primary' block>Kaydet</Button>
      </Form>
      {!hideAnswers && effectiveId && ['MULTIPLE', 'FUNCTION'].includes(String(questionType)) && (
        <>
          <Divider />
          <AnswerList
            referenceId={effectiveId}
            questionType={questionType}
            referenceType='question'
          />
        </>
      )}
      {!hideAnswers && !isTableColumnMode && id && String(questionType) === 'TABLE' && (
        <>
          <Divider />
          <React.Suspense fallback={null}>
            <TableQuestionList questionId={id} />
          </React.Suspense>
        </>
      )}
    </Spin>
  );
}
